import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { storageService } from '../services/storageService';
import { Link } from 'react-router-dom';
import { markUserInteraction } from '../utils/soundUtils';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  functional: boolean;
}

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const hasConsent = storageService.get('cookieConsent');
    if (!hasConsent) {
      setIsVisible(true);
      window.dispatchEvent(
        new CustomEvent('cookieConsentVisibilityChanged', {
          detail: { visible: true, closing: false },
        })
      );
    }
  }, []);

  const handleAcceptAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    markUserInteraction();
    if (isSaving) return;
    const newPreferences = {
      essential: true,
      analytics: true,
      functional: true,
    };
    savePreferences(newPreferences);
  };

  const handleRejectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    markUserInteraction();
    if (isSaving) return;
    const newPreferences = {
      essential: true,
      analytics: false,
      functional: false,
    };
    savePreferences(newPreferences);
  };

  const savePreferences = async (prefs: CookiePreferences) => {
    try {
      setIsSaving(true);
      setIsClosing(true);
      window.dispatchEvent(
        new CustomEvent('cookieConsentVisibilityChanged', {
          detail: { visible: true, closing: true },
        })
      );

      storageService.set('cookieConsent', {
        preferences: prefs,
        timestamp: Date.now(),
      });

      window['ga-disable-G-76NZ0KJK8Z'] = !prefs.analytics;

      window.dispatchEvent(
        new CustomEvent('cookiePreferencesChanged', {
          detail: prefs,
        })
      );

      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsVisible(false);
      window.dispatchEvent(
        new CustomEvent('cookieConsentVisibilityChanged', {
          detail: { visible: false, closing: false },
        })
      );
      setIsClosing(false);
      setIsSaving(false);
    } catch (error) {
      console.error('Error al guardar las preferencias de cookies:', error);
      setIsVisible(false);
      window.dispatchEvent(
        new CustomEvent('cookieConsentVisibilityChanged', {
          detail: { visible: false, closing: false },
        })
      );
      setIsClosing(false);
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (isVisible) {
      markUserInteraction();
    }
  }, [isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="cookie-consent-modal"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-x-0 bottom-4 px-4 sm:px-6 lg:px-8 z-50 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-w-7xl mx-auto bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="w-full sm:w-auto sm:flex-1">
                <h3 className="text-xl font-bold mb-2 flex items-center">
                  <Link
                    to="/legal#cookies"
                    className="inline-flex items-center group bg-gradient-to-r from-yellow-500 to-red-500 bg-clip-text text-transparent hover:from-yellow-600 hover:to-red-600 transition-all duration-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      markUserInteraction();
                    }}
                  >
                    <svg
                      className="w-5 h-5 mr-1.5 text-gray-600 dark:text-gray-300 group-hover:text-yellow-500 transition-colors duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                  </Link>
                  <span className="bg-gradient-to-r from-yellow-500 to-red-500 bg-clip-text text-transparent">
                    Política de Cookies
                  </span>
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>minijocs.cat</strong> utilitza cookies essencials per
                  al seu funcionament bàsic. Fem servir{' '}
                  <strong>Google Analytics</strong> per entendre com navegues i
                  així millorar el lloc. Les cookies essencials són
                  imprescindibles i no es poden desactivar. Si acceptes, ens
                  ajudaràs amb estadístiques anònimes per oferir una millor
                  experiència.
                </p>
              </div>
              <div className="flex flex-col gap-3 w-full sm:w-auto">
                <button
                  onClick={handleAcceptAll}
                  disabled={isSaving}
                  className={`w-full px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-yellow-500 to-red-500 hover:from-yellow-600 hover:to-red-600 rounded-md transition-all duration-300 ${
                    isSaving
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:scale-105'
                  }`}
                >
                  {'Acceptar'}
                </button>
                <button
                  onClick={handleRejectAll}
                  disabled={isSaving}
                  className={`w-full px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-all duration-300 ${
                    isSaving
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:scale-105'
                  }`}
                >
                  {'Només Essencials'}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
